import {
  createContext,
  FC,
  ReactElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Modal, ModalOverlay, ModalProps } from '@chakra-ui/react';

type ModalContextProps = {
  isOpen: boolean;
  closeModal: () => void;
  openModal: (Modal: ReactElement, options?: Partial<ModalProps>) => void;
  updateModalContent: (
    ModalContent: ReactElement,
    options?: Partial<ModalProps>
  ) => void;
};

const ImperativeModalContext = createContext<ModalContextProps>({
  isOpen: false,
  openModal: () => {
    return;
  },
  closeModal: () => {
    return;
  },
  updateModalContent: () => {
    return;
  },
});

export const ImperativeModalProvider: FC<{
  value?: ModalContextProps;
}> = ({ children, ...rest }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [modalOptions, setModalOptions] = useState<Partial<ModalProps>>();
  const [modalContent, setModalContent] = useState<ReactElement | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const modalOpen = (Modal: ReactElement, options?: Partial<ModalProps>) => {
    if (!isMountedRef.current) return;
    setModalContent(Modal);
    setModalOptions(options);
    setIsOpen(true);
  };

  const updateModalContent = (
    ModalContent: ReactElement,
    options?: Partial<ModalProps>
  ) => {
    if (!isMountedRef.current) return;
    setModalContent(ModalContent);
    setModalOptions(options);
  };

  const modalClose = () => {
    if (!isMountedRef.current) return;
    setIsOpen(false);
    setModalContent(null);
  };

  const openModalCallback = useCallback(modalOpen, []);
  const closeModalCallback = useCallback(modalClose, []);
  const updateModalContentCallback = useCallback(updateModalContent, []);

  const contextValue = useMemo(
    () => ({
      isOpen,
      openModal: openModalCallback,
      closeModal: closeModalCallback,
      updateModalContent: updateModalContentCallback,
    }),
    [isOpen, openModalCallback, closeModalCallback, updateModalContentCallback]
  );

  return (
    <ImperativeModalContext.Provider value={contextValue} {...rest}>
      <Modal
        size="4xl"
        {...modalOptions}
        isOpen={isOpen}
        onClose={modalClose}
        data-testid="imperative-modal"
      >
        <ModalOverlay>{modalContent}</ModalOverlay>
      </Modal>
      {children}
    </ImperativeModalContext.Provider>
  );
};

export const useImperativeModal = (): ModalContextProps => {
  const context = useContext(ImperativeModalContext);

  if (!context) {
    throw new Error('useImperativeModal must be used within the Provider');
  }

  return context;
};
